import {
  CacheScope,
  UICacheScope,
  getScopedCacheKey,
  getUIScopedCacheKey,
  getCacheKeysForScope,
  getUICacheKeysForScope,
} from "./cacheScopeManager";

const MAX_STORAGE_SIZE = 4 * 1024 * 1024;
const CACHE_VERSION = "1.0";

interface StorageMetadata {
  version: string;
  timestamp: number;
  size: number;
}

function getObjectSize(obj: any): number {
  try {
    const str = JSON.stringify(obj);
    return new Blob([str]).size;
  } catch {
    return 0;
  }
}

function getTotalStorageSize(): number {
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      const value = localStorage.getItem(key);
      if (value) {
        total += new Blob([key + value]).size;
      }
    }
  }
  return total;
}

function cleanOldCache(): void {
  const keysToRemove: string[] = [];
  const now = Date.now();
  const MAX_AGE = 7 * 24 * 60 * 60 * 1000;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("cache_")) {
      try {
        const metadataKey = `${key}_metadata`;
        const metadata = localStorage.getItem(metadataKey);
        if (metadata) {
          const parsed: StorageMetadata = JSON.parse(metadata);
          if (now - parsed.timestamp > MAX_AGE) {
            keysToRemove.push(key);
            keysToRemove.push(metadataKey);
          }
        }
      } catch {
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Silent cleanup
    }
  });
}

function emergencyCleanup(): void {
  const priorities = ["conversationMapCache", "chatListCache", "unRead_"];

  for (const prefix of priorities) {
    const removed = removeItemsByPrefix(prefix);
    if (removed > 0) {
      if (getTotalStorageSize() < MAX_STORAGE_SIZE * 0.7) {
        break;
      }
    }
  }
}

function removeItemsByPrefix(prefix: string): number {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Silent cleanup
    }
  });

  return keysToRemove.length;
}

export function safeSetItem<T>(key: string, value: T): boolean {
  try {
    const size = getObjectSize(value);

    if (size > 2 * 1024 * 1024) {
      return false;
    }

    const totalSize = getTotalStorageSize();
    if (totalSize + size > MAX_STORAGE_SIZE) {
      cleanOldCache();

      if (getTotalStorageSize() + size > MAX_STORAGE_SIZE) {
        emergencyCleanup();
      }

      if (getTotalStorageSize() + size > MAX_STORAGE_SIZE) {
        return false;
      }
    }

    localStorage.setItem(key, JSON.stringify(value));

    const metadata: StorageMetadata = {
      version: CACHE_VERSION,
      timestamp: Date.now(),
      size: size,
    };
    localStorage.setItem(`${key}_metadata`, JSON.stringify(metadata));

    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === "QuotaExceededError") {
      emergencyCleanup();
      try {
        localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
}

export function safeGetItem<T>(key: string): T | null {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return null;
    return JSON.parse(item) as T;
  } catch {
    return null;
  }
}

export function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_metadata`);
  } catch {
    // Silent fail
  }
}

export function clearAllCaches(): void {
  const cachePrefixes = [
    "chatListCache",
    "conversationMapCache",
    "cache_",
    "chat:",
  ];

  cachePrefixes.forEach((prefix) => {
    removeItemsByPrefix(prefix);
  });
}

// Scoped cache functions

/**
 * Sets an item in storage with scoped key
 * @param scope - The cache scope
 * @param key - The cache key (will be prefixed with scope)
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export function safeScopedSetItem<T>(
  scope: CacheScope,
  key: string,
  value: T,
): boolean {
  const scopedKey = getScopedCacheKey(scope, key);
  return safeSetItem(scopedKey, value);
}

/**
 * Gets an item from storage with scoped key
 * @param scope - The cache scope
 * @param key - The cache key (will be prefixed with scope)
 * @returns The stored value, or null if not found
 */
export function safeScopedGetItem<T>(scope: CacheScope, key: string): T | null {
  const scopedKey = getScopedCacheKey(scope, key);
  return safeGetItem<T>(scopedKey);
}

/**
 * Removes an item from storage with scoped key
 * @param scope - The cache scope
 * @param key - The cache key (will be prefixed with scope)
 */
export function safeScopedRemoveItem(scope: CacheScope, key: string): void {
  const scopedKey = getScopedCacheKey(scope, key);
  safeRemoveItem(scopedKey);
}

/**
 * Clears all caches for a specific scope
 * @param scope - The cache scope to clear
 * @returns Number of keys removed
 */
export function clearScopeCache(scope: CacheScope): number {
  const keys = getCacheKeysForScope(scope);

  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}_metadata`);
    } catch (error) {
      // Silent fail
    }
  });

  return keys.length;
}

/**
 * Saves an item to storage with UI scoped key (personal per user)
 * @param scope - The UI cache scope (companyId, userId)
 * @param key - The cache key (will be prefixed with ui scope)
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export function safeUIScopedSetItem<T>(
  scope: UICacheScope,
  key: string,
  value: T,
): boolean {
  const scopedKey = getUIScopedCacheKey(scope, key);
  return safeSetItem(scopedKey, value);
}

/**
 * Gets an item from storage with UI scoped key (personal per user)
 * @param scope - The UI cache scope (companyId, userId)
 * @param key - The cache key (will be prefixed with ui scope)
 * @returns The stored value, or null if not found
 */
export function safeUIScopedGetItem<T>(
  scope: UICacheScope,
  key: string,
): T | null {
  const scopedKey = getUIScopedCacheKey(scope, key);
  return safeGetItem<T>(scopedKey);
}

/**
 * Removes an item from storage with UI scoped key (personal per user)
 * @param scope - The UI cache scope (companyId, userId)
 * @param key - The cache key (will be prefixed with ui scope)
 */
export function safeUIScopedRemoveItem(scope: UICacheScope, key: string): void {
  const scopedKey = getUIScopedCacheKey(scope, key);
  safeRemoveItem(scopedKey);
}

/**
 * Clears all UI caches for a specific scope
 * @param scope - The UI cache scope to clear
 * @returns Number of keys removed
 */
export function clearUIScopeCache(scope: UICacheScope): number {
  const keys = getUICacheKeysForScope(scope);

  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // Silent fail
    }
  });

  return keys.length;
}

export function limitArraySize<T>(arr: T[], maxSize: number): T[] {
  if (arr.length <= maxSize) return arr;
  return arr.slice(0, maxSize);
}

export function limitConversationMap(
  map: Record<string, any[]>,
  maxChats: number,
  maxMessagesPerChat: number,
): Record<string, any[]> {
  const entries = Object.entries(map);

  if (entries.length <= maxChats) {
    const limited: Record<string, any[]> = {};
    entries.forEach(([key, messages]) => {
      limited[key] = messages.slice(0, maxMessagesPerChat);
    });
    return limited;
  }

  const limited: Record<string, any[]> = {};
  entries.slice(0, maxChats).forEach(([key, messages]) => {
    limited[key] = messages.slice(0, maxMessagesPerChat);
  });
  return limited;
}

export function getStorageStats() {
  return {
    used: getTotalStorageSize(),
    max: MAX_STORAGE_SIZE,
    percentage: (getTotalStorageSize() / MAX_STORAGE_SIZE) * 100,
  };
}
